import { useState, useEffect, useCallback, useRef } from 'react'
import { Users, ChevronDown, Printer, RefreshCw, AlertTriangle, Download, Calendar, Check, Filter } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

// ── Seletor de Período ────────────────────────────────────────────────────────
const iso = (d) => d.toISOString().split('T')[0]

const PERIODOS = [
  { value: 'todos',      label: 'Todos os períodos' },
  { value: 'hoje',       label: 'Hoje' },
  { value: 'semana',     label: 'Esta semana' },
  { value: 'mes',        label: 'Este mês' },
  { value: 'mes_passado', label: 'Mês passado' },
  { value: 'proximos30', label: 'Próximos 30 dias' },
  { value: 'ano',        label: 'Este ano' },
  { value: 'custom',     label: 'Personalizado' },
]

function calcRange(key) {
  const today = new Date()
  switch (key) {
    case 'hoje': return { inicio: iso(today), fim: iso(today) }
    case 'semana': {
      const start = new Date(today)
      const diff = today.getDay() === 0 ? -6 : 1 - today.getDay()
      start.setDate(today.getDate() + diff)
      return { inicio: iso(start), fim: iso(today) }
    }
    case 'mes': return {
      inicio: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
      fim: iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    }
    case 'mes_passado': return {
      inicio: iso(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      fim: iso(new Date(today.getFullYear(), today.getMonth(), 0)),
    }
    case 'proximos30': {
      const end = new Date(today)
      end.setDate(today.getDate() + 30)
      return { inicio: iso(today), fim: iso(end) }
    }
    case 'ano': return {
      inicio: iso(new Date(today.getFullYear(), 0, 1)),
      fim: iso(new Date(today.getFullYear(), 11, 31)),
    }
    default: return { inicio: '', fim: '' }
  }
}

function PeriodoPicker({ onChange, resetKey }) {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState('todos')
  const [cInicio, setCInicio] = useState('')
  const [cFim, setCFim] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    setSelected('todos'); setCInicio(''); setCFim('')
  }, [resetKey])

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  const pick = (key) => {
    setSelected(key)
    if (key !== 'custom') {
      const { inicio, fim } = calcRange(key)
      onChange(inicio, fim)
      setOpen(false)
    }
  }

  const applyCustom = () => {
    onChange(cInicio, cFim)
    setOpen(false)
  }

  const label = PERIODOS.find(p => p.value === selected)?.label || 'Período'
  const ativo = selected !== 'todos'

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-sm transition-colors outline-none ${
          ativo
            ? 'border-blue-400 bg-blue-50 text-blue-700 font-medium'
            : 'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Calendar className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{label}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden w-56">
          {PERIODOS.filter(p => p.value !== 'custom').map(p => (
            <button
              key={p.value}
              onClick={() => pick(p.value)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                selected === p.value
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {p.label}
              {selected === p.value && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
          {/* Personalizado */}
          <div className="border-t border-gray-100">
            <button
              onClick={() => setSelected('custom')}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                selected === 'custom' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Personalizado
            </button>
            {selected === 'custom' && (
              <div className="px-4 py-3 flex flex-col gap-2 bg-gray-50">
                <input type="date" value={cInicio} onChange={e => setCInicio(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full" />
                <input type="date" value={cFim} onChange={e => setCFim(e.target.value)}
                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full" />
                <button onClick={applyCustom}
                  className="bg-blue-600 text-white rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-blue-700 transition-colors">
                  Aplicar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

// Agrupa parcelas por comprador
function groupByComprador(parcelas) {
  const map = {}
  for (const p of parcelas) {
    const nome = p.comprador?.nome || `ID ${p.comprador_id}`
    if (!map[nome]) map[nome] = []
    map[nome].push(p)
  }
  return Object.entries(map).sort(([a], [b]) => a.localeCompare(b))
}

function SecaoStatus({ titulo, cor, parcelas, colunaExtra }) {
  const total = parcelas.reduce((s, p) => s + Number(p.valor || 0), 0)
  const grupos = groupByComprador(parcelas)

  const corMap = {
    amarelo: { header: 'bg-yellow-100', headerTxt: 'text-yellow-800', border: 'border-yellow-200', foot: 'bg-yellow-50', footTxt: 'text-yellow-800', rowAlt: 'bg-yellow-50/20' },
    vermelho: { header: 'bg-red-100', headerTxt: 'text-red-800', border: 'border-red-200', foot: 'bg-red-100', footTxt: 'text-red-800', rowAlt: 'bg-red-50/20' },
    verde: { header: 'bg-green-100', headerTxt: 'text-green-800', border: 'border-green-200', foot: 'bg-green-50', footTxt: 'text-green-800', rowAlt: 'bg-green-50/20' },
  }
  const c = corMap[cor]

  return (
    <div className="mb-8">
      <div className={`${c.header} px-4 py-2 rounded-t-lg flex items-center justify-between`}>
        <h3 className={`text-sm font-bold ${c.headerTxt} uppercase`}>
          {titulo} — {parcelas.length} parcela{parcelas.length !== 1 ? 's' : ''}
        </h3>
        <span className={`text-sm font-bold ${c.headerTxt}`}>{fmt(total)}</span>
      </div>

      {grupos.map(([comprador, ps], gi) => {
        const subtotal = ps.reduce((s, p) => s + Number(p.valor || 0), 0)
        return (
          <div key={comprador} className={gi > 0 ? 'mt-3' : ''}>
            <div className="bg-gray-100 px-3 py-1.5 border-x border-gray-200 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">{comprador}</span>
              <span className="text-xs font-semibold text-gray-600">{fmt(subtotal)}</span>
            </div>
            <table className={`w-full text-left border ${c.border}`}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600">Vencimento</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600">Lote</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600">Vendedor</th>
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-center">Parcela</th>
                  {colunaExtra && <th className="px-3 py-2 text-xs font-semibold text-gray-600">{colunaExtra}</th>}
                  <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {ps
                  .slice()
                  .sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''))
                  .map((p, i) => (
                    <tr key={p.id} className={i % 2 === 0 ? 'bg-white' : c.rowAlt}>
                      <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(p.data_vencimento)}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 font-mono">
                        {p.lote?.numero_lote ? `#${p.lote.numero_lote}` : `ID ${p.lote_id}`}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-700">{p.vendedor?.nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-600 text-center">
                        {p.numero_parcela}/{p.total_parcelas}
                      </td>
                      {colunaExtra && (
                        <td className="px-3 py-2 text-xs text-green-700">
                          {p.data_pagamento ? fmtDate(p.data_pagamento) : '—'}
                        </td>
                      )}
                      <td className="px-3 py-2 text-xs font-semibold text-right text-gray-800">{fmt(p.valor)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )
      })}

      <div className={`${c.foot} border border-t-0 ${c.border} px-3 py-2 flex justify-between`}>
        <span className="text-xs font-bold text-gray-700">Total {titulo}:</span>
        <span className={`text-xs font-bold ${c.footTxt}`}>{fmt(total)}</span>
      </div>
    </div>
  )
}

export default function RelatorioCompradorVendedor() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const iframeRef = useRef(null)

  const [leiloes, setLeiloes] = useState([])
  const [loadingL, setLoadingL] = useState(true)
  const [leilaoId, setLeilaoId] = useState('')
  const [filtroDataInicio, setFiltroDataInicio] = useState('')
  const [filtroDataFim, setFiltroDataFim] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('')
  const [compradorFiltro, setCompradorFiltro] = useState('')
  const [periodoResetKey, setPeriodoResetKey] = useState(0)

  const [parcelas, setParcelas] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl]         = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .catch(() => {})
      .finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId || !leilaoId) return
    setLoading(true)
    try {
      const filtros = {}
      if (leilaoId) filtros.leilao_id = leilaoId
      if (filtroDataInicio) filtros.data_inicio = filtroDataInicio
      if (filtroDataFim) filtros.data_fim = filtroDataFim
      if (statusFiltro) filtros.status = statusFiltro
      const res = await gestaoService.listarParcelasNegociacao(orgId, filtros)
      setParcelas(res.data || res)
    } catch {
      toast.error('Erro ao carregar parcelas')
    } finally {
      setLoading(false)
    }
  }, [orgId, leilaoId, filtroDataInicio, filtroDataFim, statusFiltro])

  useEffect(() => {
    if (orgId && leilaoId) carregar()
  }, [orgId, leilaoId, carregar])

  const parcelasFiltradas = compradorFiltro
    ? parcelas.filter(p => String(p.comprador_id) === compradorFiltro)
    : parcelas

  const emAberto = parcelasFiltradas.filter(p => p.status === 'em_aberto')
  const atrasado = parcelasFiltradas.filter(p => p.status === 'atrasado')
  const pago     = parcelasFiltradas.filter(p => p.status === 'pago')

  const totalGeral   = parcelasFiltradas.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalAberto   = emAberto.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalAtrasado = atrasado.reduce((s, p) => s + Number(p.valor || 0), 0)
  const totalPago     = pago.reduce((s, p) => s + Number(p.valor || 0), 0)

  // Lista de compradores únicos derivada das parcelas carregadas (para o select)
  const compradoresDisponiveis = [...new Map(
    parcelas
      .filter(p => p.comprador_id && (p.comprador?.nome || p.comprador_id))
      .map(p => [p.comprador_id, { id: p.comprador_id, nome: p.comprador?.nome || `ID ${p.comprador_id}` }])
  ).values()].sort((a, b) => a.nome.localeCompare(b.nome))

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const filtroInfo = leilaoAtual ? leilaoAtual.nome : 'Todos os leilões'

  const closePdfModal = () => {
    setShowPdfModal(false)
    if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) }
  }

  const printPdf = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus()
      iframeRef.current.contentWindow.print()
    }
  }

  const handleImprimir = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const filtros = {}
      if (leilaoId) filtros.leilao_id = leilaoId
      if (filtroDataInicio) filtros.data_inicio = filtroDataInicio
      if (filtroDataFim) filtros.data_fim = filtroDataFim
      if (statusFiltro) filtros.status = statusFiltro
      if (compradorFiltro) filtros.comprador_id = compradorFiltro
      const res = await gestaoService.relatorioCompradorVendedorPdf(orgId, filtros)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar o relatório.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="no-print flex items-center gap-2">
        <Users className="w-5 h-5 text-amber-600" />
        <h1 className="text-lg font-semibold text-gray-800">Relatório Comprador x Vendedor</h1>
      </div>

      {/* Filtros */}
      <div className="no-print bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700">Filtros</span>
          {(statusFiltro || filtroDataInicio || filtroDataFim || compradorFiltro) && (
            <button onClick={() => { setStatusFiltro(''); setCompradorFiltro(''); setFiltroDataInicio(''); setFiltroDataFim(''); setPeriodoResetKey(k => k + 1) }}
              className="ml-auto text-xs text-blue-600 hover:text-blue-800">
              Limpar filtros
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <select value={leilaoId} onChange={e => { setLeilaoId(e.target.value); setCompradorFiltro('') }} disabled={loadingL}
            className="col-span-2 md:col-span-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white">
            <option value="">Todos os leilões</option>
            {leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </select>

          <select value={compradorFiltro} onChange={e => setCompradorFiltro(e.target.value)}
            className="col-span-2 md:col-span-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white">
            <option value="">Todos os compradores</option>
            {compradoresDisponiveis.map(c => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
          </select>

          <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-white">
            <option value="">Todos os status</option>
            <option value="em_aberto">Em Aberto</option>
            <option value="atrasado">Atrasado</option>
            <option value="pago">Pago</option>
          </select>

          <PeriodoPicker
            resetKey={periodoResetKey}
            onChange={(inicio, fim) => { setFiltroDataInicio(inicio); setFiltroDataFim(fim) }}
          />

          <div className="flex items-center gap-2">
            <button onClick={carregar}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
              Filtrar
            </button>
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin" />}
            <button onClick={handleImprimir} disabled={!parcelas.length || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors ml-auto">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
        </div>
      ) : parcelas.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhuma parcela encontrada com os filtros selecionados
        </div>
      ) : (
        <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          {/* Cabeçalho */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-800 gap-4">
            <div className="w-28 flex-shrink-0">
              {organizadorAtivo?.logo_url
                ? <img src={organizadorAtivo.logo_url} alt="Logo" className="max-h-14 max-w-full object-contain" />
                : <span className="text-xs font-bold text-gray-700">{organizadorAtivo?.name}</span>}
            </div>
            <div className="text-center flex-1">
              <h2 className="text-xl font-black uppercase tracking-wide text-gray-900">Relatório Comprador x Vendedor</h2>
              <p className="text-sm text-gray-600 mt-0.5">{organizadorAtivo?.name}</p>
              <p className="text-sm text-gray-500 mt-0.5">{filtroInfo}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Emitido em {new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} às{' '}
                {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
              </p>
            </div>
            <div className="w-28 flex-shrink-0" />
          </div>

          {/* Cards resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-xs text-blue-600 font-medium">Total Geral</p>
              <p className="text-lg font-bold text-blue-800">{fmt(totalGeral)}</p>
              <p className="text-xs text-blue-500">{parcelas.length} parcelas</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-xs text-yellow-600 font-medium">Em Aberto</p>
              <p className="text-lg font-bold text-yellow-800">{fmt(totalAberto)}</p>
              <p className="text-xs text-yellow-500">{emAberto.length} parcelas</p>
            </div>
            <div className={`rounded-lg p-4 text-center border ${atrasado.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-center gap-1 mb-0.5">
                {atrasado.length > 0 && <AlertTriangle className="w-3 h-3 text-red-500" />}
                <p className={`text-xs font-medium ${atrasado.length > 0 ? 'text-red-600' : 'text-gray-500'}`}>Atrasado</p>
              </div>
              <p className={`text-lg font-bold ${atrasado.length > 0 ? 'text-red-800' : 'text-gray-700'}`}>{fmt(totalAtrasado)}</p>
              <p className={`text-xs ${atrasado.length > 0 ? 'text-red-500' : 'text-gray-400'}`}>{atrasado.length} parcelas</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-xs text-green-600 font-medium">Pago</p>
              <p className="text-lg font-bold text-green-800">{fmt(totalPago)}</p>
              <p className="text-xs text-green-500">{pago.length} parcelas</p>
            </div>
          </div>

          {/* Atrasado primeiro — maior urgência */}
          {atrasado.length > 0 && (
            <SecaoStatus titulo="Atrasado" cor="vermelho" parcelas={atrasado} />
          )}

          {emAberto.length > 0 && (
            <SecaoStatus titulo="Em Aberto" cor="amarelo" parcelas={emAberto} />
          )}

          {pago.length > 0 && (
            <SecaoStatus titulo="Pago" cor="verde" parcelas={pago} colunaExtra="Data Pagamento" />
          )}

          {/* Rodapé totais */}
          <div className="mt-6 pt-4 border-t-2 border-gray-800 flex flex-wrap justify-between gap-4 text-sm">
            <div className="flex flex-wrap gap-6">
              {atrasado.length > 0 && (
                <span className="text-red-700 font-semibold">Atrasado: {fmt(totalAtrasado)}</span>
              )}
              {emAberto.length > 0 && (
                <span className="text-yellow-700 font-semibold">Em Aberto: {fmt(totalAberto)}</span>
              )}
              {pago.length > 0 && (
                <span className="text-green-700 font-semibold">Pago: {fmt(totalPago)}</span>
              )}
            </div>
            <span className="font-black text-gray-900">Total Geral: {fmt(totalGeral)}</span>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Relatório Comprador x Vendedor</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, 'Relatorio_Comprador_Vendedor.pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Download className="w-4 h-4" /> Salvar PDF
                </button>
                <button onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Relatório Comprador x Vendedor"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
